import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "./booksDetailsPage.css";
import BaseNavbar from "../../commanComponents/navbar/baseNavbar";
import Footer from "../../commanComponents/footer/footer";
import SkeletonLoading from "../../commanComponents/skeletonLoading/skeletonLoading";
import { BookDetailsService, bookAddToCartService } from "../../../modules/services";
import { ROUTES } from "../../../constant";
import { clearData, loadData } from "../../../utils/localStorage";

const DEFAULT_PHYSICAL_SHIPPING = 90;
const PURCHASE_MODES = {
  digital: "digital",
  physical_bundle: "physical_bundle",
};

const formatPrice = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const BookDetailsPage = () => {
  const [activeTab, setActiveTab] = useState("description");
  const [selectedPurchaseMode, setSelectedPurchaseMode] = useState(PURCHASE_MODES.digital);
  const [loading, setLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const [bookDetails, setBookDetails] = useState(null);

  const { slug } = useParams();
  const navigate = useNavigate();
  const accessToken = loadData("access");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const responseData = await BookDetailsService(slug, {});

      if (responseData === "error" || !responseData.data) {
        clearData();
        navigate(ROUTES.LOGIN);
        return;
      }

      if (responseData.status_code !== 200 && responseData.status_code !== 201) {
        setLoading(false);
        return;
      }

      const results = responseData.data;
      setBookDetails(results);
      setIsInCart(results.is_add_to_cart || false);
      setLoading(false);
    })();
  }, [slug, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll(".books-details-fade-in");
      elements.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        if (elementTop < window.innerHeight - 150) {
          element.classList.add("visible");
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const formatGradeLevels = (gradeLevels) => {
    if (!gradeLevels || gradeLevels.length === 0) return "";
    return gradeLevels.map((grade) => `Class ${grade.name}`).join(", ");
  };

  const pricingSummary = useMemo(() => {
    const digitalPrice = Number(bookDetails?.price || 0);
    const shippingCost =
      selectedPurchaseMode === PURCHASE_MODES.physical_bundle
        ? DEFAULT_PHYSICAL_SHIPPING
        : 0;

    return {
      digitalPrice,
      shippingCost,
      total: digitalPrice + shippingCost,
    };
  }, [bookDetails?.price, selectedPurchaseMode]);

  const purchaseModeLabel =
    selectedPurchaseMode === PURCHASE_MODES.physical_bundle
      ? "Physical + E-Book"
      : "E-Book";

  const handleAddToCart = () => {
    if (!accessToken) {
      navigate(ROUTES.LOGIN);
      return;
    }

    if (isInCart) {
      navigate(ROUTES.BOOKS_SELLING_CART_PAGE);
      return;
    }

    (async () => {
      setCartLoading(true);
      const responseData = await bookAddToCartService(
        {
          book_id: bookDetails.id,
          purchase_type: selectedPurchaseMode,
        },
        {}
      );

      if (responseData === "error") {
        clearData();
        navigate(ROUTES.LOGIN);
        return;
      }

      if (responseData.status_code !== 200 && responseData.status_code !== 201) {
        setCartLoading(false);
        return;
      }

      setIsInCart(true);
      setBookDetails({ ...bookDetails, is_add_to_cart: true });
      setCartLoading(false);
      window.dispatchEvent(new Event("cartUpdated"));
    })();
  };

  const handleBuyNow = () => {
    navigate(
      `${ROUTES.BOOKS_SELLING_CHECKOUT_PAGE}?book_id=${bookDetails.id}&purchase_type=${selectedPurchaseMode}`
    );
  };

  const handleButtonClick = (event, action) => {
    event.preventDefault();
    const button = event.currentTarget;
    button.style.transform = "scale(0.95)";
    window.setTimeout(() => {
      button.style.transform = "";
    }, 150);

    if (!accessToken) {
      navigate(ROUTES.LOGIN);
      return;
    }

    if (action === "add-to-cart") {
      handleAddToCart();
    } else if (action === "buy-now") {
      handleBuyNow();
    }
  };

  useEffect(() => {
    const cards = document.querySelectorAll(".books-details-book-card");
    cards.forEach((card, index) => {
      card.style.opacity = "0";
      card.style.transform = "translateY(20px)";
      card.style.transition = "all 0.6s ease";
      window.setTimeout(() => {
        card.style.opacity = "1";
        card.style.transform = "translateY(0)";
      }, index * 100 + 500);
    });
  }, [bookDetails]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        const tabs = document.querySelectorAll(".books-details-tab-btn");
        const currentIndex = Array.from(tabs).findIndex((tab) =>
          tab.classList.contains("active")
        );

        const nextIndex =
          event.key === "ArrowRight"
            ? (currentIndex + 1) % tabs.length
            : (currentIndex - 1 + tabs.length) % tabs.length;

        tabs[nextIndex].click();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <>
      <BaseNavbar activeItem="BOOKS" />
      <div className="books-details-container">
        <section className="books-details-book-detail-hero books-details-fade-in">
          <div className="books-details-book-hero-content">
            <div className="books-details-book-image-section">
              {loading ? (
                <SkeletonLoading customStyle={{ width: "100%", height: "400px" }} />
              ) : (
                <>
                  <div
                    className="books-details-book-image"
                    style={{
                      backgroundImage: bookDetails?.cover_image_url
                        ? `url(${bookDetails.cover_image_url})`
                        : "none",
                    }}
                  ></div>
                  <div className="books-details-book-badges">
                    {bookDetails?.is_featured && (
                      <span className="books-details-book-badge bestseller">Featured</span>
                    )}
                    {bookDetails?.is_published && (
                      <span className="books-details-book-badge">Published</span>
                    )}
                    {bookDetails?.has_discount && (
                      <span className="books-details-book-badge sale">Sale</span>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="books-details-book-info">
              {loading ? (
                <>
                  <SkeletonLoading
                    customStyle={{ width: "200px", height: "20px", marginBottom: "10px" }}
                  />
                  <SkeletonLoading
                    customStyle={{ width: "100%", height: "40px", marginBottom: "10px" }}
                  />
                  <SkeletonLoading
                    customStyle={{ width: "250px", height: "20px", marginBottom: "15px" }}
                  />
                  <SkeletonLoading
                    customStyle={{ width: "300px", height: "25px", marginBottom: "15px" }}
                  />
                  <SkeletonLoading
                    customStyle={{ width: "200px", height: "35px", marginBottom: "20px" }}
                  />
                  <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
                    <SkeletonLoading customStyle={{ width: "100px", height: "60px" }} />
                    <SkeletonLoading customStyle={{ width: "100px", height: "60px" }} />
                    <SkeletonLoading customStyle={{ width: "100px", height: "60px" }} />
                  </div>
                </>
              ) : (
                <>
                  <div className="books-details-book-category">
                    {bookDetails?.subject} • {formatGradeLevels(bookDetails?.grade_level)}
                  </div>
                  <h1 className="books-details-book-title">{bookDetails?.title}</h1>
                  {bookDetails?.subtitle && (
                    <div className="books-details-book-subtitle">{bookDetails.subtitle}</div>
                  )}
                  <div className="books-details-book-author">
                    by {bookDetails?.created_by?.first_name} {bookDetails?.created_by?.last_name}
                  </div>

                  <div className="books-details-book-rating">
                    <div className="books-details-stars">
                      {[...Array(5)].map((_, index) => (
                        <div
                          key={index}
                          className="books-details-star"
                          style={{
                            opacity:
                              index <
                              Math.floor(parseFloat(bookDetails?.average_rating || 0))
                                ? 1
                                : 0.3,
                          }}
                        ></div>
                      ))}
                    </div>
                    <span className="books-details-rating-text">
                      {bookDetails?.average_rating} out of 5 ({bookDetails?.total_reviews} reviews)
                    </span>
                  </div>

                  <div className="books-details-format-showcase">
                    <div className="books-details-format-showcase-head">
                      <span className="books-details-format-kicker">Choose your format</span>
                      <h3>Buy the way your learner studies best</h3>
                      <p>
                        Every book is available as an instant E-Book or as a Physical +
                        E-Book bundle.
                      </p>
                    </div>

                    <div className="books-details-format-grid">
                      <button
                        type="button"
                        className={`books-details-format-card ${
                          selectedPurchaseMode === PURCHASE_MODES.digital ? "is-active" : ""
                        }`}
                        onClick={() => setSelectedPurchaseMode(PURCHASE_MODES.digital)}
                      >
                        <span className="books-details-format-badge">E-Book</span>
                        <strong>Instant digital access</strong>
                        <p>Perfect for quick learning on any device.</p>
                        <div className="books-details-format-price">
                          {formatPrice(bookDetails?.price)}
                        </div>
                        <ul className="books-details-format-points">
                          <li>Reader access after payment</li>
                          <li>Study from dashboard anytime</li>
                          <li>No shipping added</li>
                        </ul>
                      </button>

                      <button
                        type="button"
                        className={`books-details-format-card ${
                          selectedPurchaseMode === PURCHASE_MODES.physical_bundle
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() => setSelectedPurchaseMode(PURCHASE_MODES.physical_bundle)}
                      >
                        <span className="books-details-format-badge bundle">
                          Physical + E-Book
                        </span>
                        <strong>Printed copy plus digital access</strong>
                        <p>Best for students who want desk study and reader flexibility.</p>
                        <div className="books-details-format-price">
                          {formatPrice(pricingSummary.total)}
                        </div>
                        <ul className="books-details-format-points">
                          <li>Printed book delivery</li>
                          <li>Full digital reader access included</li>
                          <li>{formatPrice(pricingSummary.shippingCost)} shipping applied</li>
                        </ul>
                      </button>
                    </div>
                  </div>

                  <div className="books-details-purchase-summary">
                    <div className="books-details-purchase-summary-head">
                      <div>
                        <span className="books-details-purchase-summary-label">
                          Selected format
                        </span>
                        <strong>{purchaseModeLabel}</strong>
                      </div>
                      <div className="books-details-purchase-summary-total">
                        <span>Total</span>
                        <strong>{formatPrice(pricingSummary.total)}</strong>
                      </div>
                    </div>

                    <div className="books-details-purchase-breakdown">
                      <div>
                        <span>Book price</span>
                        <strong>{formatPrice(pricingSummary.digitalPrice)}</strong>
                      </div>
                      <div>
                        <span>Shipping</span>
                        <strong>
                          {pricingSummary.shippingCost
                            ? formatPrice(pricingSummary.shippingCost)
                            : "Included"}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="books-details-book-specs">
                    <div className="books-details-spec-item">
                      <div className="books-details-spec-label">Chapters</div>
                      <div className="books-details-spec-value">{bookDetails?.total_chapters}</div>
                    </div>
                    <div className="books-details-spec-item">
                      <div className="books-details-spec-label">Lessons</div>
                      <div className="books-details-spec-value">{bookDetails?.total_lessons}</div>
                    </div>
                    <div className="books-details-spec-item">
                      <div className="books-details-spec-label">Purchases</div>
                      <div className="books-details-spec-value">{bookDetails?.total_purchases}</div>
                    </div>
                    <div className="books-details-spec-item">
                      <div className="books-details-spec-label">Delivery</div>
                      <div className="books-details-spec-value">
                        {selectedPurchaseMode === PURCHASE_MODES.physical_bundle
                          ? "Print + Digital"
                          : "Digital Only"}
                      </div>
                    </div>
                  </div>

                  <div className="books-details-action-buttons">
                    <button
                      className="books-details-btn books-details-btn-primary"
                      onClick={(event) => handleButtonClick(event, "add-to-cart")}
                      disabled={bookDetails?.is_purchased || cartLoading}
                    >
                      {cartLoading
                        ? "Adding..."
                        : bookDetails?.is_purchased
                        ? "Purchased"
                        : isInCart
                        ? "View Cart"
                        : `Add ${purchaseModeLabel} to Cart`}
                    </button>
                    <button
                      className="books-details-btn books-details-btn-secondary"
                      onClick={(event) => handleButtonClick(event, "buy-now")}
                      disabled={bookDetails?.is_purchased}
                    >
                      Buy {purchaseModeLabel} Now
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="books-details-tabs books-details-fade-in">
          <div className="books-details-tab-nav">
            <button
              className={`books-details-tab-btn ${activeTab === "description" ? "active" : ""}`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={`books-details-tab-btn ${activeTab === "features" ? "active" : ""}`}
              onClick={() => setActiveTab("features")}
            >
              Key Features
            </button>
            <button
              className={`books-details-tab-btn ${activeTab === "reviews" ? "active" : ""}`}
              onClick={() => setActiveTab("reviews")}
            >
              Reviews
            </button>
            <button
              className={`books-details-tab-btn ${activeTab === "contents" ? "active" : ""}`}
              onClick={() => setActiveTab("contents")}
            >
              Contents
            </button>
          </div>

          <div className="books-details-tab-content">
            {loading ? (
              <div style={{ padding: "20px" }}>
                <SkeletonLoading
                  customStyle={{ width: "200px", height: "25px", marginBottom: "15px" }}
                />
                <SkeletonLoading
                  customStyle={{ width: "100%", height: "15px", marginBottom: "10px" }}
                />
                <SkeletonLoading
                  customStyle={{ width: "100%", height: "15px", marginBottom: "10px" }}
                />
                <SkeletonLoading
                  customStyle={{ width: "90%", height: "15px", marginBottom: "10px" }}
                />
                <SkeletonLoading
                  customStyle={{ width: "95%", height: "15px", marginBottom: "10px" }}
                />
              </div>
            ) : (
              <>
                {activeTab === "description" && (
                  <div className="books-details-tab-panel active">
                    <div className="books-details-description">
                      <h3>About This Book</h3>
                      <p>{bookDetails?.description}</p>
                      {bookDetails?.short_description && (
                        <>
                          <br />
                          <p>{bookDetails?.short_description}</p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "features" && (
                  <div className="books-details-tab-panel active">
                    <div className="books-details-features-grid">
                      <div className="books-details-feature-card">
                        <div className="books-details-feature-icon">Bk</div>
                        <div className="books-details-feature-title">Complete Coverage</div>
                        <div>All topics covered comprehensively</div>
                      </div>
                      <div className="books-details-feature-card">
                        <div className="books-details-feature-icon">SS</div>
                        <div className="books-details-feature-title">Step-by-Step Solutions</div>
                        <div>Detailed explanations for every problem and concept</div>
                      </div>
                      <div className="books-details-feature-card">
                        <div className="books-details-feature-icon">PQ</div>
                        <div className="books-details-feature-title">Practice Questions</div>
                        <div>1000+ practice questions with varying difficulty levels</div>
                      </div>
                      <div className="books-details-feature-card">
                        <div className="books-details-feature-icon">EF</div>
                        <div className="books-details-feature-title">Exam Focused</div>
                        <div>Previous year papers and sample tests included</div>
                      </div>
                      <div className="books-details-feature-card">
                        <div className="books-details-feature-icon">EA</div>
                        <div className="books-details-feature-title">Expert Authors</div>
                        <div>Written by experienced teachers</div>
                      </div>
                      <div className="books-details-feature-card">
                        <div className="books-details-feature-icon">PR</div>
                        <div className="books-details-feature-title">Proven Results</div>
                        <div>Trusted by thousands of students nationwide</div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "reviews" && (
                  <div className="books-details-tab-panel active">
                    <div className="books-details-reviews-section">
                      <div className="books-details-review-card">
                        <div className="books-details-reviewer-info">
                          <div className="books-details-reviewer-avatar">AS</div>
                          <div>
                            <div className="books-details-reviewer-name">Arjun Sharma</div>
                            <div className="books-details-review-role">Class 10 Student</div>
                          </div>
                        </div>
                        <div className="books-details-review-text">
                          "This book made learning so much easier for me. The explanations
                          helped me understand concepts I was struggling with."
                        </div>
                      </div>
                      <div className="books-details-review-card">
                        <div className="books-details-reviewer-info">
                          <div className="books-details-reviewer-avatar">PR</div>
                          <div>
                            <div className="books-details-reviewer-name">Priya Reddy</div>
                            <div className="books-details-review-role">Teacher</div>
                          </div>
                        </div>
                        <div className="books-details-review-text">
                          "The progression from basic to advanced concepts is excellent,
                          and the format works beautifully for guided study."
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "contents" && (
                  <div className="books-details-tab-panel active">
                    <div className="books-details-description">
                      <h3>Table of Contents</h3>
                      {bookDetails?.chapters && bookDetails.chapters.length > 0 ? (
                        bookDetails.chapters.map((chapter) => (
                          <p key={chapter.id}>
                            <strong>Chapter {chapter.order}:</strong> {chapter.title}
                            {chapter.description ? ` - ${chapter.description}` : ""}
                            {chapter.lesson_count ? ` (${chapter.lesson_count} lessons)` : ""}
                          </p>
                        ))
                      ) : (
                        <p>No chapters available</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <section className="books-details-cta-section books-details-fade-in">
          {loading ? (
            <div style={{ textAlign: "center" }}>
              <SkeletonLoading
                customStyle={{ width: "400px", height: "35px", margin: "0 auto 15px" }}
              />
              <SkeletonLoading
                customStyle={{ width: "500px", height: "20px", margin: "0 auto 20px" }}
              />
            </div>
          ) : (
            <>
              <h2>Ready to study your way?</h2>
              <p>
                Choose E-Book for instant access or select the Physical + E-Book
                bundle for a printed copy plus digital learning.
              </p>
              <div className="books-details-cta-buttons">
                <button
                  className="books-details-btn"
                  onClick={(event) => handleButtonClick(event, "add-to-cart")}
                  disabled={bookDetails?.is_purchased || cartLoading}
                >
                  {cartLoading
                    ? "Adding to Cart..."
                    : bookDetails?.is_purchased
                    ? "Already Purchased"
                    : isInCart
                    ? "View Cart"
                    : `Add ${purchaseModeLabel} - ${formatPrice(pricingSummary.total)}`}
                </button>
                <button
                  className="books-details-btn"
                  onClick={(event) => handleButtonClick(event, "buy-now")}
                  disabled={bookDetails?.is_purchased}
                >
                  Buy Now - {formatPrice(pricingSummary.total)}
                </button>
              </div>
              <div className="books-details-trust-indicators">
                <div>Instant digital access</div>
                <div>Secure payment</div>
                <div>Printed delivery option</div>
                <div>Student-friendly support</div>
              </div>
            </>
          )}
        </section>
      </div>
      <Footer />
    </>
  );
};

export default BookDetailsPage;
